import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Switch } from "@/components/ui/switch";
import {
  Loader2,
  ChevronLeft,
  Pencil,
  Camera,
  CalendarIcon,
  Phone,
  Mail,
  User,
  Briefcase,
  Plus,
  Trash2,
  Clock,
  AlertCircle,
  X,
} from "lucide-react";
import { useStaffRoles, useToggleStaffStatus } from "@/hooks/useStaffList";
import { format } from "date-fns";
import { cn } from "@/lib/utils";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useStaffDetail, useUpdateStaff } from "@/hooks/useStaffEdit";
import { useClinics } from "@/hooks/useClinics";
import { useAuthStore } from "@/stores/authStore";
import { CONSOLE_PERMISSIONS } from "@/constants/permissions";
import { AccessDenied } from "@/components/AccessDenied";
import { getApiErrorStatus } from "@/lib/api";
import { toast } from "sonner";

// --- Availability slot types ---

interface AvailabilitySlot {
  day: string;
  start_time: string;
  end_time: string;
  location: string;
  mode: string;
  clinic_id: string | null;
}

const DAYS_OF_WEEK = [
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
  "sunday",
] as const;

const DAY_LABELS: Record<string, string> = {
  monday: "Monday",
  tuesday: "Tuesday",
  wednesday: "Wednesday",
  thursday: "Thursday",
  friday: "Friday",
  saturday: "Saturday",
  sunday: "Sunday",
};

const DAY_SHORT_LABELS: Record<string, string> = {
  monday: "Mon",
  tuesday: "Tue",
  wednesday: "Wed",
  thursday: "Thu",
  friday: "Fri",
  saturday: "Sat",
  sunday: "Sun",
};

// --- Professional info entry types ---

interface ExperienceEntry {
  institution_name: string;
  designation: string;
  location: string;
  start_date: string;
  end_date: string;
}

interface EducationEntry {
  institution_name: string;
  degree: string;
  location: string;
  start_year: string;
  end_year: string;
}

interface CertificationEntry {
  title: string;
  year: string;
  school_name: string;
  location: string;
  certificate_link: string;
}

// --- Constants ---

const CLINICAL_ROLES: readonly string[] = [
  "doctor",
  "nutritionist",
  "physiotherapist",
];

const GENDER_OPTIONS = [
  { value: "M", label: "Male" },
  { value: "F", label: "Female" },
  { value: "O", label: "Other" },
] as const;

const normalizeGender = (
  value?: string | null
): "M" | "F" | "O" | undefined => {
  const v = value?.trim().toLowerCase();
  if (v === "m" || v === "male") return "M";
  if (v === "f" || v === "female") return "F";
  if (v === "o" || v === "other") return "O";
  return undefined;
};

// --- Zod Schema ---

const COUNTRY_CODES = [
  { value: "+91", label: "IN +91", flag: "\u{1F1EE}\u{1F1F3}" },
  { value: "+1", label: "US +1", flag: "\u{1F1FA}\u{1F1F8}" },
] as const;

const createStaffSchema = z.object({
  firstName: z.string().min(1, "First name is required"),
  lastName: z.string().min(1, "Last name is required"),
  countryCode: z.string().min(1, "Country code is required"),
  phoneNumber: z
    .string()
    .min(10, "Phone number must be at least 10 digits")
    .regex(/^\d+$/, "Phone number must contain only digits"),
  email: z.string().email("Invalid email address").optional().or(z.literal("")),
  gender: z.enum(["M", "F", "O"], { required_error: "Gender is required" }),
  dob: z.date({ required_error: "Date of birth is required" }),
  userType: z.string().min(1, "Staff role is required"),
  designation: z.string().min(1, "Designation is required"),
  experience: z
    .string()
    .min(1, "Experience is required")
    .regex(/^\d+$/, "Experience must be a number"),
  languages: z.string().optional(),
  about: z.string().optional(),
});

type CreateStaffFormValues = z.infer<typeof createStaffSchema>;

// --- Default blank entries ---

const BLANK_EXP: ExperienceEntry = {
  institution_name: "",
  designation: "",
  location: "",
  start_date: "",
  end_date: "",
};

const BLANK_EDU: EducationEntry = {
  institution_name: "",
  degree: "",
  location: "",
  start_year: "",
  end_year: "",
};

const BLANK_CERT: CertificationEntry = {
  title: "",
  year: "",
  school_name: "",
  location: "",
  certificate_link: "",
};

// --- Avatar helpers ---

const getInitials = (first: string, last: string) => {
  return `${first?.[0] || ""}${last?.[0] || ""}`.toUpperCase();
};

const getAvatarColor = (name: string) => {
  const colors = [
    "from-emerald-500 to-emerald-600",
    "from-teal-500 to-teal-600",
    "from-success to-success",
    "from-cyan-600 to-cyan-700",
    "from-stone-500 to-stone-600",
    "from-amber-600 to-amber-700",
    "from-slate-500 to-slate-600",
    "from-lime-600 to-lime-700",
  ];
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  return colors[Math.abs(hash) % colors.length];
};

// --- Component ---

export const EditStaffForm = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const staffId = searchParams.get("id") || "";
  const { hasPermission } = useAuthStore();

  const { data: staffDetail, isLoading, error } = useStaffDetail(staffId);
  const {
    data: staffRoles,
    isLoading: areStaffRolesLoading,
    error: staffRolesError,
    refetch: refetchStaffRoles,
  } = useStaffRoles();
  const { data: clinics } = useClinics();
  const updateMutation = useUpdateStaff();
  const toggleMutation = useToggleStaffStatus();
  const [toggleConfirmOpen, setToggleConfirmOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);

  // Local optimistic state for the status pill — synced from server when not mid-toggle
  const [localIsActive, setLocalIsActive] = useState<boolean>(
    () => staffDetail?.is_active ?? true
  );
  useEffect(() => {
    if (staffDetail != null && !toggleMutation.isPending) {
      setLocalIsActive(staffDetail.is_active);
    }
  }, [staffDetail?.is_active, toggleMutation.isPending]);

  // Photo state — three distinct states for edit context
  const [profilePhoto, setProfilePhoto] = useState<File | null>(null);
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);
  const [_existingPhotoUrl, setExistingPhotoUrl] = useState<string | null>(
    null
  );
  const [photoRemoved, setPhotoRemoved] = useState(false);

  const [successOpen, setSuccessOpen] = useState(false);
  const [errorOpen, setErrorOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>("");

  // Professional info array state (managed outside react-hook-form — lives in profile.meta)
  const [experiences, setExperiences] = useState<ExperienceEntry[]>([]);
  const [educations, setEducations] = useState<EducationEntry[]>([]);
  const [certifications, setCertifications] = useState<CertificationEntry[]>(
    []
  );

  // Inline add-form visibility and temporary entry state
  const [showExpForm, setShowExpForm] = useState(false);
  const [newExp, setNewExp] = useState<ExperienceEntry>({ ...BLANK_EXP });

  const [showEduForm, setShowEduForm] = useState(false);
  const [newEdu, setNewEdu] = useState<EducationEntry>({ ...BLANK_EDU });

  const [showCertForm, setShowCertForm] = useState(false);
  const [newCert, setNewCert] = useState<CertificationEntry>({ ...BLANK_CERT });

  // Working hours slot state
  const [slots, setSlots] = useState<AvailabilitySlot[]>([]);
  const [addingForDay, setAddingForDay] = useState("");
  const [slotStart, setSlotStart] = useState("");
  const [slotEnd, setSlotEnd] = useState("");
  const [slotMode, setSlotMode] = useState("teleconsult");
  const [slotClinicId, setSlotClinicId] = useState<string | null>(null);
  const [slotError, setSlotError] = useState("");
  const [activeTab, setActiveTab] = useState("profile");

  const form = useForm<CreateStaffFormValues>({
    resolver: zodResolver(createStaffSchema),
    defaultValues: {
      firstName: "",
      lastName: "",
      countryCode: "+91",
      phoneNumber: "",
      email: "",
      gender: undefined,
      dob: undefined,
      userType: undefined,
      designation: "",
      experience: "",
      languages: "",
      about: "",
    },
  });

  // Pre-populate all form fields when staff detail loads
  useEffect(() => {
    if (!staffDetail) return;

    const profile = staffDetail.user_profile || {};
    const meta = profile.meta || {};

    // Parse phone: strip country code prefix if stored with it
    let countryCode = "+91";
    let phone = staffDetail.phone_number || "";
    for (const cc of COUNTRY_CODES) {
      if (phone.startsWith(cc.value)) {
        countryCode = cc.value;
        phone = phone.slice(cc.value.length);
        break;
      }
    }

    form.reset({
      firstName: staffDetail.first_name || "",
      lastName: staffDetail.last_name || "",
      countryCode,
      phoneNumber: phone,
      email: staffDetail.email || "",
      gender: normalizeGender(profile.gender),
      dob: profile.dob ? new Date(profile.dob) : undefined,
      userType: staffDetail.user_type,
      designation: profile.designation || "",
      experience: profile.experience != null ? String(profile.experience) : "",
      languages: (meta.languages_known || []).join(", "),
      about: meta.about || "",
    });

    // Pre-populate professional info arrays
    setExperiences(meta.previous_experience || []);
    setEducations(meta.education || []);
    setCertifications(meta.certifications || []);

    // Pre-populate availability slots (slot IDs not preserved — backend recreates on PUT)
    // Legacy slots (created before mode/clinic support) default to teleconsult/null.
    setSlots(
      (staffDetail.slots || []).map((s) => ({
        day: s.day,
        start_time:
          typeof s.start_time === "string"
            ? s.start_time
            : String(s.start_time),
        end_time:
          typeof s.end_time === "string" ? s.end_time : String(s.end_time),
        location: s.location || "Tele-Consultations",
        mode: s.mode || "teleconsult",
        clinic_id: s.clinic_id ?? null,
      }))
    );

    // Pre-populate photo preview from remote URL
    if (staffDetail.profile_pic) {
      setPhotoPreview(staffDetail.profile_pic);
      setExistingPhotoUrl(staffDetail.profile_pic);
    }
  }, [staffDetail, form]);

  const watchedUserType = form.watch("userType");
  const isClinical = CLINICAL_ROLES.includes(watchedUserType);
  const getRoleLabel = (roleName: string) =>
    staffRoles?.find((role) => role.name === roleName)?.label;

  const { dirtyFields } = form.formState;
  /** Returns a ring class when a field has been modified in edit mode */
  const dirtyRing = (field: keyof CreateStaffFormValues) =>
    isEditing && dirtyFields[field] ? "ring-2 ring-primary/40" : "";

  // --- Photo handlers ---

  const handlePhotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setProfilePhoto(file);
      setPhotoPreview(URL.createObjectURL(file));
      setPhotoRemoved(false);
    }
  };

  const handleRemovePhoto = () => {
    setProfilePhoto(null);
    setPhotoPreview(null);
    setExistingPhotoUrl(null);
    setPhotoRemoved(true);
  };

  // --- Experience handlers ---

  const handleSaveExp = () => {
    if (
      !newExp.institution_name.trim() ||
      !newExp.designation.trim() ||
      !newExp.location.trim()
    )
      return;
    setExperiences((prev) => [...prev, { ...newExp }]);
    setNewExp({ ...BLANK_EXP });
    setShowExpForm(false);
  };

  const handleRemoveExp = (index: number) => {
    setExperiences((prev) => prev.filter((_, i) => i !== index));
  };

  // --- Education handlers ---

  const handleSaveEdu = () => {
    if (
      !newEdu.institution_name.trim() ||
      !newEdu.degree.trim() ||
      !newEdu.location.trim()
    )
      return;
    setEducations((prev) => [...prev, { ...newEdu }]);
    setNewEdu({ ...BLANK_EDU });
    setShowEduForm(false);
  };

  const handleRemoveEdu = (index: number) => {
    setEducations((prev) => prev.filter((_, i) => i !== index));
  };

  // --- Certification handlers ---

  const handleSaveCert = () => {
    if (!newCert.title.trim()) return;
    setCertifications((prev) => [...prev, { ...newCert }]);
    setNewCert({ ...BLANK_CERT });
    setShowCertForm(false);
  };

  const handleRemoveCert = (index: number) => {
    setCertifications((prev) => prev.filter((_, i) => i !== index));
  };

  // --- Working hours handlers ---

  const timeToMinutes = (timeStr: string): number => {
    const [hours, minutes] = timeStr.split(":").map(Number);
    return hours * 60 + (minutes || 0);
  };

  const validateSlotOverlap = (
    day: string,
    startTime: string,
    endTime: string,
    mode: string,
    clinicId: string | null
  ): string | null => {
    const startMin = timeToMinutes(startTime);
    const endMin = timeToMinutes(endTime);

    if (startMin >= endMin) {
      return "End time must be after start time";
    }

    for (const existing of slots) {
      if (
        existing.day === day &&
        existing.mode === mode &&
        existing.clinic_id === clinicId
      ) {
        const existStart = timeToMinutes(existing.start_time);
        const existEnd = timeToMinutes(existing.end_time);
        if (startMin < existEnd && existStart < endMin) {
          return `Overlaps with existing ${DAY_LABELS[day]} slot (${existing.start_time.slice(0, 5)} - ${existing.end_time.slice(0, 5)})`;
        }
      }
    }
    return null;
  };

  const handleAddSlot = () => {
    setSlotError("");
    if (!addingForDay || !slotStart || !slotEnd) {
      setSlotError("Start time and end time are required");
      return;
    }
    if (slotMode === "clinic" && !slotClinicId) {
      setSlotError("Select a clinic");
      return;
    }
    const startFormatted =
      slotStart.length === 5 ? `${slotStart}:00` : slotStart;
    const endFormatted = slotEnd.length === 5 ? `${slotEnd}:00` : slotEnd;
    const clinicId = slotMode === "clinic" ? slotClinicId : null;

    const error = validateSlotOverlap(
      addingForDay,
      startFormatted,
      endFormatted,
      slotMode,
      clinicId
    );
    if (error) {
      setSlotError(error);
      return;
    }

    const clinicName =
      slotMode === "clinic" && clinicId
        ? (clinics?.find((c) => c.id === clinicId)?.name ?? "In-Person")
        : "Tele-Consultations";

    setSlots((prev) => [
      ...prev,
      {
        day: addingForDay,
        start_time: startFormatted,
        end_time: endFormatted,
        location: clinicName,
        mode: slotMode,
        clinic_id: clinicId,
      },
    ]);
    setAddingForDay("");
    setSlotStart("");
    setSlotEnd("");
    setSlotClinicId(null);
  };

  const handleRemoveSlot = (index: number) => {
    setSlots((prev) => prev.filter((_, i) => i !== index));
  };

  const onInvalid = () => {
    setActiveTab("profile");
    toast.error("Please fix the errors on the Profile tab before saving.");
  };

  // --- Submit handler ---

  const handleSubmit = async (values: CreateStaffFormValues) => {
    if (!staffRoles?.some((role) => role.name === values.userType)) {
      form.setError("userType", {
        message: "This staff member has an unknown or unavailable role.",
      });
      return;
    }

    const formData = new FormData();
    formData.append("first_name", values.firstName);
    formData.append("last_name", values.lastName);
    formData.append(
      "phone_number",
      `${values.countryCode}${values.phoneNumber}`
    );
    // The backend rejects a blank email, so omit the field when it is empty.
    // Note: this means a cleared email is left unchanged rather than cleared —
    // clearing needs the backend to accept null/blank on this endpoint.
    if (values.email) {
      formData.append("email", values.email);
    }

    const profile = {
      gender: values.gender,
      experience: parseInt(values.experience),
      designation: values.designation,
      dob: format(values.dob, "yyyy-MM-dd"),
      meta: {
        languages_known: values.languages
          ? values.languages
              .split(",")
              .map((l) => l.trim())
              .filter(Boolean)
          : [],
        about: values.about || "",
        previous_experience: isClinical ? experiences : [],
        certifications: isClinical ? certifications : [],
        education: isClinical ? educations : [],
      },
    };
    formData.append("profile", JSON.stringify(profile));
    formData.append("slots", JSON.stringify(slots));

    // Photo handling for PUT:
    // - New file uploaded → send it
    // - User explicitly removed photo → send empty string to clear it
    // - No change → omit profile_pic field (backend keeps existing)
    if (profilePhoto) {
      formData.append("profile_pic", profilePhoto);
    } else if (photoRemoved) {
      formData.append("profile_pic", "");
    }

    updateMutation.mutate(
      { id: staffId, formData },
      {
        onSuccess: () => {
          setIsEditing(false);
          setSuccessOpen(true);
        },
        onError: (err: any) => {
          // Map DRF field-specific errors to form errors
          if (err?.details) {
            const fieldMap: Record<string, string> = {
              first_name: "firstName",
              last_name: "lastName",
              phone_number: "phoneNumber",
              email: "email",
              gender: "gender",
              dob: "dob",
              user_type: "userType",
              designation: "designation",
              experience: "experience",
            };
            Object.entries(err.details).forEach(([field, messages]) => {
              const formField = fieldMap[field];
              if (formField) {
                form.setError(formField as any, {
                  message: Array.isArray(messages)
                    ? (messages[0] as string)
                    : String(messages),
                });
              }
            });
          }
          setErrorMessage(
            err?.status === 403
              ? "Your permissions no longer allow staff profile changes."
              : err?.message ||
                  "Failed to update staff member. Please try again."
          );
          setErrorOpen(true);
        },
      }
    );
  };

  // --- Success dialog handler ---

  const handleSuccessOk = () => {
    setSuccessOpen(false);
    navigate("/settings/staff/view-staff");
  };

  // --- Loading and error states ---

  if (!hasPermission(CONSOLE_PERMISSIONS.MANAGE_STAFF)) {
    return (
      <AccessDenied description="You do not have permission to manage staff members." />
    );
  }

  if (!staffId) {
    return (
      <Card className="border-none shadow-none">
        <CardContent className="py-16">
          <div className="flex flex-col items-center gap-3 text-center">
            <div className="w-14 h-14 rounded-2xl bg-destructive/10 flex items-center justify-center">
              <AlertCircle className="h-7 w-7 text-destructive" />
            </div>
            <h3 className="z-h3 text-foreground">No Staff ID</h3>
            <p className="z-body text-muted-foreground max-w-xs">
              No staff member ID provided. Please navigate from the staff list.
            </p>
            <Button
              variant="outline"
              onClick={() => navigate("/settings/staff/view-staff")}
            >
              Back to Staff List
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (isLoading || areStaffRolesLoading) {
    return (
      <Card className="border-none shadow-none">
        <CardContent className="py-16">
          <div className="flex items-center justify-center gap-3">
            <Loader2 className="h-7 w-7 animate-spin text-primary" />
            <span className="z-body text-muted-foreground">
              Loading staff details...
            </span>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (
    getApiErrorStatus(error) === 403 ||
    getApiErrorStatus(staffRolesError) === 403
  ) {
    return (
      <AccessDenied description="Your current permissions do not allow access to staff management." />
    );
  }

  if (error || staffRolesError || !staffDetail || !staffRoles?.length) {
    return (
      <Card className="border-none shadow-none">
        <CardContent className="py-16">
          <div className="flex flex-col items-center gap-3 text-center">
            <div className="w-14 h-14 rounded-2xl bg-destructive/10 flex items-center justify-center">
              <AlertCircle className="h-7 w-7 text-destructive" />
            </div>
            <h3 className="z-h3 text-foreground">Failed to Load</h3>
            <p className="z-body text-muted-foreground max-w-xs">
              {staffRolesError
                ? "Failed to load the canonical staff role catalog."
                : "Failed to load staff member details."}
            </p>
            <div className="flex gap-2">
              {staffRolesError ? (
                <Button variant="outline" onClick={() => refetchStaffRoles()}>
                  Retry
                </Button>
              ) : null}
              <Button
                variant="outline"
                onClick={() => navigate("/settings/staff/view-staff")}
              >
                Back to Staff List
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  const fullName = `${staffDetail.first_name} ${staffDetail.last_name}`;

  // --- Schedule tab: split slots into Telehealth / In Clinic views ---

  const sortSlots = (list: AvailabilitySlot[]) =>
    [...list].sort((a, b) => {
      const dayOrder = DAYS_OF_WEEK.indexOf(
        a.day as (typeof DAYS_OF_WEEK)[number]
      );
      const dayOrderB = DAYS_OF_WEEK.indexOf(
        b.day as (typeof DAYS_OF_WEEK)[number]
      );
      if (dayOrder !== dayOrderB) return dayOrder - dayOrderB;
      return a.start_time.localeCompare(b.start_time);
    });

  const hasClinicSlots = slots.some((s) => s.mode === "clinic");
  const hasClinics = (clinics?.length ?? 0) > 0 || hasClinicSlots;
  const teleconsultSlots = sortSlots(
    slots.filter((s) => s.mode === "teleconsult")
  );
  const clinicSlots = sortSlots(slots.filter((s) => s.mode === "clinic"));
  const knownClinicIds = new Set((clinics ?? []).map((c) => c.id));
  const orphanClinicIds = Array.from(
    new Set(
      clinicSlots
        .map((s) => s.clinic_id)
        .filter((id): id is string => !!id && !knownClinicIds.has(id))
    )
  );
  const clinicSlotGroups = [
    ...(clinics ?? []).map((c) => ({
      clinicId: c.id,
      clinicName: c.name,
      slots: clinicSlots.filter((s) => s.clinic_id === c.id),
    })),
    ...orphanClinicIds.map((id) => ({
      clinicId: id,
      clinicName: "Unknown clinic",
      slots: clinicSlots.filter((s) => s.clinic_id === id),
    })),
  ].filter((group) => group.slots.length > 0);

  const findSlotIndex = (slot: AvailabilitySlot) =>
    slots.findIndex(
      (s) =>
        s.day === slot.day &&
        s.start_time === slot.start_time &&
        s.end_time === slot.end_time &&
        s.mode === slot.mode &&
        s.clinic_id === slot.clinic_id
    );

  const openAddSlotForDay = (mode: string, day: string) => {
    setAddingForDay(day);
    setSlotStart("");
    setSlotEnd("");
    setSlotMode(mode);
    setSlotClinicId(null);
    setSlotError("");
  };

  return (
    <>
      {/* Profile identity card — single row: chevron | avatar | identity | actions */}
      <Card className="mb-6">
        <CardContent className="p-5">
          <div className="flex items-start gap-3">
            {/* Back chevron — inline with avatar, matches patient profile pattern */}
            <Button
              variant="outline"
              size="icon"
              className="h-9 w-9 shrink-0 rounded-md bg-background"
              onClick={() => navigate("/settings/staff/view-staff")}
            >
              <ChevronLeft className="h-4 w-4 text-foreground" />
            </Button>

            {/* Avatar with upload overlay */}
            <div className="relative flex-shrink-0">
              <div className="flex flex-col items-center gap-1">
                <div
                  className={cn(
                    "relative w-14 h-14 rounded-full overflow-hidden",
                    isEditing && "cursor-pointer group"
                  )}
                  onClick={() =>
                    isEditing &&
                    document.getElementById("edit-profile-photo-input")?.click()
                  }
                >
                  {photoPreview ? (
                    <img
                      src={photoPreview}
                      alt="Profile photo"
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div
                      className={cn(
                        "w-full h-full bg-gradient-to-br text-white flex items-center justify-center text-xl font-bold",
                        getAvatarColor(fullName)
                      )}
                    >
                      {getInitials(
                        staffDetail.first_name,
                        staffDetail.last_name
                      )}
                    </div>
                  )}
                  {isEditing && (
                    <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                      <Camera className="h-4 w-4 text-white" />
                    </div>
                  )}
                </div>
                {isEditing && photoPreview && (
                  <button
                    type="button"
                    onClick={handleRemovePhoto}
                    className="text-xs text-destructive hover:text-destructive/80 hover:underline transition-colors"
                  >
                    Remove photo
                  </button>
                )}
              </div>
              <input
                id="edit-profile-photo-input"
                type="file"
                accept="image/*"
                className="hidden"
                onChange={handlePhotoChange}
              />
            </div>

            {/* Staff identity info */}
            <div className="flex-1 min-w-0">
              <h2 className="z-h2 text-foreground leading-tight">{fullName}</h2>
              <div className="flex items-center gap-2 flex-wrap mt-1">
                <span className="z-body text-muted-foreground">
                  {staffDetail.user_type
                    ? (getRoleLabel(staffDetail.user_type) ??
                      staffDetail.user_type)
                    : "—"}
                </span>
                {/* Status toggle */}
                <div className="flex items-center gap-2">
                  {toggleMutation.isPending ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />
                  ) : (
                    <Switch
                      checked={localIsActive}
                      onCheckedChange={() => setToggleConfirmOpen(true)}
                      disabled={toggleMutation.isPending}
                      className={cn(
                        localIsActive
                          ? "data-[state=checked]:bg-success"
                          : "data-[state=unchecked]:bg-destructive/60"
                      )}
                    />
                  )}
                  <span
                    className={cn(
                      "z-secondary font-medium",
                      localIsActive ? "text-success" : "text-destructive"
                    )}
                  >
                    {localIsActive ? "Active" : "Inactive"}
                  </span>
                </div>
              </div>
            </div>

            {/* Edit / Save+Cancel actions — right-aligned, top-aligned */}
            <div className="flex items-start gap-2 flex-shrink-0 ml-auto">
              {isEditing ? (
                <>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      form.reset();
                      if (staffDetail) {
                        const meta = staffDetail.user_profile?.meta || {};
                        setExperiences(meta.previous_experience || []);
                        setEducations(meta.education || []);
                        setCertifications(meta.certifications || []);
                        setSlots(
                          (staffDetail.slots || []).map((s) => ({
                            day: s.day,
                            start_time:
                              typeof s.start_time === "string"
                                ? s.start_time
                                : String(s.start_time),
                            end_time:
                              typeof s.end_time === "string"
                                ? s.end_time
                                : String(s.end_time),
                            location: s.location || "Tele-Consultations",
                            mode: s.mode || "teleconsult",
                            clinic_id: s.clinic_id ?? null,
                          }))
                        );
                        setProfilePhoto(null);
                        setPhotoRemoved(false);
                        setPhotoPreview(staffDetail.profile_pic || null);
                        setExistingPhotoUrl(staffDetail.profile_pic || null);
                      }
                      setIsEditing(false);
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    className="gap-2"
                    disabled={updateMutation.isPending}
                    onClick={form.handleSubmit(handleSubmit, onInvalid)}
                  >
                    {updateMutation.isPending && (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    )}
                    Save
                  </Button>
                </>
              ) : (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="gap-2"
                  onClick={() => setIsEditing(true)}
                >
                  <Pencil className="h-4 w-4" />
                  Edit Profile
                </Button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      <Form {...form}>
        <form
          onSubmit={form.handleSubmit(handleSubmit, onInvalid)}
          className="space-y-6"
        >
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList>
              <TabsTrigger value="profile">Profile</TabsTrigger>
              {isClinical && (
                <TabsTrigger value="professional">
                  Professional Info
                </TabsTrigger>
              )}
              <TabsTrigger value="schedule">Schedule</TabsTrigger>
            </TabsList>

            {/* Profile */}
            <TabsContent value="profile" className="mt-4">
              <Card>
                <CardHeader className="pb-4">
                  <CardTitle className="z-h3 text-foreground">
                    Personal Information
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Staff Role — read-only after creation */}
                  <FormField
                    control={form.control}
                    name="userType"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Staff Role</FormLabel>
                        <div className="h-10 flex items-center px-3 border border-input rounded-md bg-muted z-body text-muted-foreground">
                          {field.value
                            ? (getRoleLabel(field.value) ?? field.value)
                            : "—"}
                        </div>
                        <p className="z-secondary text-muted-foreground">
                          Staff role cannot be changed after creation.
                        </p>
                      </FormItem>
                    )}
                  />

                  {/* First Name & Last Name */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <FormField
                      control={form.control}
                      name="firstName"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="flex items-center gap-2">
                            <User className="h-4 w-4" />
                            First Name *
                          </FormLabel>
                          <FormControl>
                            <Input
                              placeholder="Enter first name"
                              disabled={!isEditing}
                              className={dirtyRing("firstName")}
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="lastName"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="flex items-center gap-2">
                            <User className="h-4 w-4" />
                            Last Name *
                          </FormLabel>
                          <FormControl>
                            <Input
                              placeholder="Enter last name"
                              disabled={!isEditing}
                              className={dirtyRing("lastName")}
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  {/* Phone Number & Email */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <FormField
                      control={form.control}
                      name="phoneNumber"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="flex items-center gap-2">
                            <Phone className="h-4 w-4" />
                            Phone Number *
                          </FormLabel>
                          <FormControl>
                            <div className="flex gap-2">
                              <FormField
                                control={form.control}
                                name="countryCode"
                                render={({ field: ccField }) => (
                                  <Select
                                    value={ccField.value}
                                    onValueChange={ccField.onChange}
                                    disabled={!isEditing}
                                  >
                                    <SelectTrigger
                                      className={cn(
                                        "w-32",
                                        dirtyRing("countryCode")
                                      )}
                                    >
                                      <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                      {COUNTRY_CODES.map((cc) => (
                                        <SelectItem
                                          key={cc.value}
                                          value={cc.value}
                                        >
                                          {cc.flag} {cc.label}
                                        </SelectItem>
                                      ))}
                                    </SelectContent>
                                  </Select>
                                )}
                              />
                              <Input
                                placeholder="Enter phone number"
                                disabled={!isEditing}
                                {...field}
                                className={cn(
                                  "flex-1",
                                  dirtyRing("phoneNumber")
                                )}
                              />
                            </div>
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="email"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="flex items-center gap-2">
                            <Mail className="h-4 w-4" />
                            Email (Optional)
                          </FormLabel>
                          <FormControl>
                            <Input
                              type="email"
                              placeholder="Enter email address"
                              disabled={!isEditing}
                              className={dirtyRing("email")}
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  {/* Gender & DOB */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <FormField
                      control={form.control}
                      name="gender"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Gender *</FormLabel>
                          <Select
                            onValueChange={field.onChange}
                            value={field.value}
                            disabled={!isEditing}
                          >
                            <FormControl>
                              <SelectTrigger className={dirtyRing("gender")}>
                                <SelectValue placeholder="Select gender" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              {GENDER_OPTIONS.map((option) => (
                                <SelectItem
                                  key={option.value}
                                  value={option.value}
                                >
                                  {option.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="dob"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Date of Birth *</FormLabel>
                          <Popover>
                            <PopoverTrigger asChild>
                              <FormControl>
                                <Button
                                  variant="outline"
                                  disabled={!isEditing}
                                  className={cn(
                                    "w-full justify-start text-left font-normal",
                                    !field.value && "text-muted-foreground",
                                    dirtyRing("dob")
                                  )}
                                >
                                  <CalendarIcon className="mr-2 h-4 w-4" />
                                  {field.value ? (
                                    format(field.value, "PPP")
                                  ) : (
                                    <span>Pick a date</span>
                                  )}
                                </Button>
                              </FormControl>
                            </PopoverTrigger>
                            <PopoverContent
                              className="w-auto p-0"
                              align="start"
                            >
                              <Calendar
                                mode="single"
                                selected={field.value}
                                onSelect={field.onChange}
                                disabled={(date) => {
                                  const today = new Date();
                                  today.setHours(0, 0, 0, 0);
                                  return date > today;
                                }}
                                initialFocus
                                defaultMonth={new Date(1990, 0)}
                                captionLayout="dropdown"
                                startMonth={new Date(1940, 0)}
                                endMonth={
                                  new Date(new Date().getFullYear(), 11)
                                }
                              />
                            </PopoverContent>
                          </Popover>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  {/* Designation & Experience */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <FormField
                      control={form.control}
                      name="designation"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="flex items-center gap-2">
                            <Briefcase className="h-4 w-4" />
                            Designation *
                          </FormLabel>
                          <FormControl>
                            <Input
                              placeholder="e.g. Senior Cardiologist"
                              disabled={!isEditing}
                              className={dirtyRing("designation")}
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="experience"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Experience (years) *</FormLabel>
                          <FormControl>
                            <Input
                              placeholder="e.g. 5"
                              disabled={!isEditing}
                              className={dirtyRing("experience")}
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  {/* Languages */}
                  <FormField
                    control={form.control}
                    name="languages"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Languages Known (Optional)</FormLabel>
                        <FormControl>
                          <Input
                            placeholder="e.g. English, Hindi, Tamil"
                            disabled={!isEditing}
                            className={dirtyRing("languages")}
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  {/* About */}
                  <FormField
                    control={form.control}
                    name="about"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>About (Optional)</FormLabel>
                        <FormControl>
                          <Textarea
                            placeholder="Brief description about the staff member..."
                            className={cn("resize-none", dirtyRing("about"))}
                            rows={3}
                            disabled={!isEditing}
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </CardContent>
              </Card>
            </TabsContent>

            {/* Professional Info — only for clinical roles */}
            {isClinical && (
              <TabsContent value="professional" className="mt-4">
                <Card>
                  <CardHeader className="pb-4">
                    <CardTitle className="z-h3 text-foreground">
                      Professional Information
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    {/* Experience sub-section */}
                    <div>
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="z-body-md text-foreground">
                          Experience
                        </h4>
                        {isEditing && (
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              setNewExp({ ...BLANK_EXP });
                              setShowExpForm(true);
                            }}
                            className="h-8 gap-1.5"
                          >
                            <Plus className="h-3.5 w-3.5" />
                            Add
                          </Button>
                        )}
                      </div>

                      {experiences.length > 0 && (
                        <div className="space-y-2 mb-3">
                          {experiences.map((exp, index) => (
                            <div
                              key={index}
                              className="flex items-center justify-between p-3 bg-muted/50 rounded-lg border border-border"
                            >
                              <div>
                                <span className="z-body-md text-foreground">
                                  {exp.designation}
                                </span>
                                <span className="z-body text-muted-foreground">
                                  {" "}
                                  at{" "}
                                </span>
                                <span className="z-body-md text-foreground">
                                  {exp.institution_name}
                                </span>
                                {(exp.start_date || exp.end_date) && (
                                  <span className="z-secondary text-muted-foreground ml-1.5">
                                    ({exp.start_date}
                                    {exp.end_date ? ` – ${exp.end_date}` : ""})
                                  </span>
                                )}
                              </div>
                              {isEditing && (
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => handleRemoveExp(index)}
                                  className="h-8 w-8 flex-shrink-0 text-destructive hover:text-destructive hover:bg-destructive/10"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              )}
                            </div>
                          ))}
                        </div>
                      )}

                      {showExpForm && (
                        <div className="border rounded-lg p-4 space-y-3 bg-muted/50">
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <div>
                              <label className="z-secondary text-muted-foreground mb-1.5 block">
                                Organization Name *
                              </label>
                              <Input
                                placeholder="e.g. Apollo Hospitals"
                                value={newExp.institution_name}
                                onChange={(e) =>
                                  setNewExp((prev) => ({
                                    ...prev,
                                    institution_name: e.target.value,
                                  }))
                                }
                              />
                            </div>
                            <div>
                              <label className="z-secondary text-muted-foreground mb-1.5 block">
                                Designation *
                              </label>
                              <Input
                                placeholder="e.g. Senior Doctor"
                                value={newExp.designation}
                                onChange={(e) =>
                                  setNewExp((prev) => ({
                                    ...prev,
                                    designation: e.target.value,
                                  }))
                                }
                              />
                            </div>
                            <div>
                              <label className="z-secondary text-muted-foreground mb-1.5 block">
                                Location *
                              </label>
                              <Input
                                placeholder="e.g. Mumbai"
                                value={newExp.location}
                                onChange={(e) =>
                                  setNewExp((prev) => ({
                                    ...prev,
                                    location: e.target.value,
                                  }))
                                }
                              />
                            </div>
                            <div className="grid grid-cols-2 gap-2">
                              <div>
                                <label className="z-secondary text-muted-foreground mb-1.5 block">
                                  Start Date
                                </label>
                                <Input
                                  placeholder="e.g. Jan 2020"
                                  value={newExp.start_date}
                                  onChange={(e) =>
                                    setNewExp((prev) => ({
                                      ...prev,
                                      start_date: e.target.value,
                                    }))
                                  }
                                />
                              </div>
                              <div>
                                <label className="z-secondary text-muted-foreground mb-1.5 block">
                                  End Date
                                </label>
                                <Input
                                  placeholder="e.g. Dec 2023"
                                  value={newExp.end_date}
                                  onChange={(e) =>
                                    setNewExp((prev) => ({
                                      ...prev,
                                      end_date: e.target.value,
                                    }))
                                  }
                                />
                              </div>
                            </div>
                          </div>
                          <div className="flex gap-2 justify-end">
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                setShowExpForm(false);
                                setNewExp({ ...BLANK_EXP });
                              }}
                            >
                              Cancel
                            </Button>
                            <Button
                              type="button"
                              size="sm"
                              onClick={handleSaveExp}
                              disabled={
                                !newExp.institution_name.trim() ||
                                !newExp.designation.trim() ||
                                !newExp.location.trim()
                              }
                            >
                              Save
                            </Button>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Education sub-section */}
                    <div>
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="z-body-md text-foreground">Education</h4>
                        {isEditing && (
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              setNewEdu({ ...BLANK_EDU });
                              setShowEduForm(true);
                            }}
                            className="h-8 gap-1.5"
                          >
                            <Plus className="h-3.5 w-3.5" />
                            Add
                          </Button>
                        )}
                      </div>

                      {educations.length > 0 && (
                        <div className="space-y-2 mb-3">
                          {educations.map((edu, index) => (
                            <div
                              key={index}
                              className="flex items-center justify-between p-3 bg-muted/50 rounded-lg border border-border"
                            >
                              <div>
                                <span className="z-body-md text-foreground">
                                  {edu.degree}
                                </span>
                                <span className="z-body text-muted-foreground">
                                  {" "}
                                  at{" "}
                                </span>
                                <span className="z-body-md text-foreground">
                                  {edu.institution_name}
                                </span>
                                {(edu.start_year || edu.end_year) && (
                                  <span className="z-secondary text-muted-foreground ml-1.5">
                                    ({edu.start_year}
                                    {edu.end_year ? ` – ${edu.end_year}` : ""})
                                  </span>
                                )}
                              </div>
                              {isEditing && (
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => handleRemoveEdu(index)}
                                  className="h-8 w-8 flex-shrink-0 text-destructive hover:text-destructive hover:bg-destructive/10"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              )}
                            </div>
                          ))}
                        </div>
                      )}

                      {showEduForm && (
                        <div className="border rounded-lg p-4 space-y-3 bg-muted/50">
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <div>
                              <label className="z-secondary text-muted-foreground mb-1.5 block">
                                Institution Name *
                              </label>
                              <Input
                                placeholder="e.g. AIIMS Delhi"
                                value={newEdu.institution_name}
                                onChange={(e) =>
                                  setNewEdu((prev) => ({
                                    ...prev,
                                    institution_name: e.target.value,
                                  }))
                                }
                              />
                            </div>
                            <div>
                              <label className="z-secondary text-muted-foreground mb-1.5 block">
                                Degree *
                              </label>
                              <Input
                                placeholder="e.g. MBBS"
                                value={newEdu.degree}
                                onChange={(e) =>
                                  setNewEdu((prev) => ({
                                    ...prev,
                                    degree: e.target.value,
                                  }))
                                }
                              />
                            </div>
                            <div>
                              <label className="z-secondary text-muted-foreground mb-1.5 block">
                                Location *
                              </label>
                              <Input
                                placeholder="e.g. New Delhi"
                                value={newEdu.location}
                                onChange={(e) =>
                                  setNewEdu((prev) => ({
                                    ...prev,
                                    location: e.target.value,
                                  }))
                                }
                              />
                            </div>
                            <div className="grid grid-cols-2 gap-2">
                              <div>
                                <label className="z-secondary text-muted-foreground mb-1.5 block">
                                  Start Year
                                </label>
                                <Input
                                  placeholder="e.g. 2018"
                                  value={newEdu.start_year}
                                  onChange={(e) =>
                                    setNewEdu((prev) => ({
                                      ...prev,
                                      start_year: e.target.value,
                                    }))
                                  }
                                />
                              </div>
                              <div>
                                <label className="z-secondary text-muted-foreground mb-1.5 block">
                                  End Year
                                </label>
                                <Input
                                  placeholder="e.g. 2024"
                                  value={newEdu.end_year}
                                  onChange={(e) =>
                                    setNewEdu((prev) => ({
                                      ...prev,
                                      end_year: e.target.value,
                                    }))
                                  }
                                />
                              </div>
                            </div>
                          </div>
                          <div className="flex gap-2 justify-end">
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                setShowEduForm(false);
                                setNewEdu({ ...BLANK_EDU });
                              }}
                            >
                              Cancel
                            </Button>
                            <Button
                              type="button"
                              size="sm"
                              onClick={handleSaveEdu}
                              disabled={
                                !newEdu.institution_name.trim() ||
                                !newEdu.degree.trim() ||
                                !newEdu.location.trim()
                              }
                            >
                              Save
                            </Button>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Certifications sub-section */}
                    <div>
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="z-body-md text-foreground">
                          Certifications
                        </h4>
                        {isEditing && (
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              setNewCert({ ...BLANK_CERT });
                              setShowCertForm(true);
                            }}
                            className="h-8 gap-1.5"
                          >
                            <Plus className="h-3.5 w-3.5" />
                            Add
                          </Button>
                        )}
                      </div>

                      {certifications.length > 0 && (
                        <div className="space-y-2 mb-3">
                          {certifications.map((cert, index) => (
                            <div
                              key={index}
                              className="flex items-center justify-between p-3 bg-muted/50 rounded-lg border border-border"
                            >
                              <div>
                                <span className="z-body-md text-foreground">
                                  {cert.title}
                                </span>
                                {cert.school_name && (
                                  <>
                                    {" "}
                                    &mdash;{" "}
                                    <span className="z-body-md text-foreground">
                                      {cert.school_name}
                                    </span>
                                  </>
                                )}
                                {cert.year && (
                                  <span className="z-secondary text-muted-foreground ml-1.5">
                                    ({cert.year})
                                  </span>
                                )}
                              </div>
                              {isEditing && (
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => handleRemoveCert(index)}
                                  className="h-8 w-8 flex-shrink-0 text-destructive hover:text-destructive hover:bg-destructive/10"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              )}
                            </div>
                          ))}
                        </div>
                      )}

                      {showCertForm && (
                        <div className="border rounded-lg p-4 space-y-3 bg-muted/50">
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <div>
                              <label className="z-secondary text-muted-foreground mb-1.5 block">
                                Title *
                              </label>
                              <Input
                                placeholder="e.g. ACLS Certification"
                                value={newCert.title}
                                onChange={(e) =>
                                  setNewCert((prev) => ({
                                    ...prev,
                                    title: e.target.value,
                                  }))
                                }
                              />
                            </div>
                            <div>
                              <label className="z-secondary text-muted-foreground mb-1.5 block">
                                Year
                              </label>
                              <Input
                                placeholder="e.g. 2024"
                                value={newCert.year}
                                onChange={(e) =>
                                  setNewCert((prev) => ({
                                    ...prev,
                                    year: e.target.value,
                                  }))
                                }
                              />
                            </div>
                            <div>
                              <label className="z-secondary text-muted-foreground mb-1.5 block">
                                Organization
                              </label>
                              <Input
                                placeholder="e.g. American Heart Association"
                                value={newCert.school_name}
                                onChange={(e) =>
                                  setNewCert((prev) => ({
                                    ...prev,
                                    school_name: e.target.value,
                                  }))
                                }
                              />
                            </div>
                            <div>
                              <label className="z-secondary text-muted-foreground mb-1.5 block">
                                Location
                              </label>
                              <Input
                                placeholder="e.g. Online"
                                value={newCert.location}
                                onChange={(e) =>
                                  setNewCert((prev) => ({
                                    ...prev,
                                    location: e.target.value,
                                  }))
                                }
                              />
                            </div>
                            <div className="sm:col-span-2">
                              <label className="z-secondary text-muted-foreground mb-1.5 block">
                                Certificate Link (Optional)
                              </label>
                              <Input
                                placeholder="e.g. https://..."
                                value={newCert.certificate_link}
                                onChange={(e) =>
                                  setNewCert((prev) => ({
                                    ...prev,
                                    certificate_link: e.target.value,
                                  }))
                                }
                              />
                            </div>
                          </div>
                          <div className="flex gap-2 justify-end">
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                setShowCertForm(false);
                                setNewCert({ ...BLANK_CERT });
                              }}
                            >
                              Cancel
                            </Button>
                            <Button
                              type="button"
                              size="sm"
                              onClick={handleSaveCert}
                              disabled={!newCert.title.trim()}
                            >
                              Save
                            </Button>
                          </div>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>
            )}

            {/* Schedule */}
            <TabsContent value="schedule" className="mt-4">
              <Card>
                <CardHeader className="pb-4">
                  <CardTitle className="z-h3 text-foreground flex items-center gap-2">
                    <Clock className="h-4 w-4 text-muted-foreground" />
                    Schedule
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <Tabs defaultValue="telehealth">
                    <TabsList>
                      <TabsTrigger value="telehealth">Telehealth</TabsTrigger>
                      {hasClinics && (
                        <TabsTrigger value="clinic">In Clinic</TabsTrigger>
                      )}
                    </TabsList>

                    {/* Telehealth — weekly column view */}
                    <TabsContent value="telehealth" className="mt-4 space-y-1">
                      {DAYS_OF_WEEK.map((day) => {
                        const daySlots = teleconsultSlots.filter(
                          (s) => s.day === day
                        );
                        const isAdding =
                          addingForDay === day && slotMode === "teleconsult";

                        return (
                          <div
                            key={day}
                            className="border rounded-lg overflow-hidden"
                          >
                            <div className="flex items-center p-3 gap-3">
                              <span className="w-12 shrink-0 text-sm font-medium">
                                {DAY_SHORT_LABELS[day]}
                              </span>

                              {daySlots.length === 0 ? (
                                <span className="text-sm text-muted-foreground flex-1">
                                  Unavailable
                                </span>
                              ) : (
                                <div className="flex flex-wrap gap-1.5 flex-1">
                                  {daySlots.map((slot, i) => {
                                    const idx = findSlotIndex(slot);
                                    return (
                                      <div
                                        key={i}
                                        className="flex items-center gap-1.5 bg-muted rounded-md px-2.5 py-1"
                                      >
                                        <span className="text-sm">
                                          {slot.start_time.slice(0, 5)} –{" "}
                                          {slot.end_time.slice(0, 5)}
                                        </span>
                                        {isEditing && (
                                          <button
                                            type="button"
                                            onClick={() =>
                                              handleRemoveSlot(idx)
                                            }
                                            className="text-muted-foreground hover:text-destructive transition-colors"
                                          >
                                            <X className="h-3 w-3" />
                                          </button>
                                        )}
                                      </div>
                                    );
                                  })}
                                </div>
                              )}

                              {isEditing && (
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="icon"
                                  onClick={() =>
                                    openAddSlotForDay("teleconsult", day)
                                  }
                                  className="h-7 w-7 shrink-0"
                                >
                                  <Plus className="h-3.5 w-3.5" />
                                </Button>
                              )}
                            </div>

                            {isAdding && (
                              <div className="border-t bg-muted/50 p-3 space-y-2">
                                <div className="flex items-end gap-2">
                                  <div className="flex-1">
                                    <label className="z-secondary text-muted-foreground mb-1 block">
                                      Start Time
                                    </label>
                                    <Input
                                      type="time"
                                      value={slotStart}
                                      onChange={(e) =>
                                        setSlotStart(e.target.value)
                                      }
                                    />
                                  </div>
                                  <div className="flex-1">
                                    <label className="z-secondary text-muted-foreground mb-1 block">
                                      End Time
                                    </label>
                                    <Input
                                      type="time"
                                      value={slotEnd}
                                      onChange={(e) =>
                                        setSlotEnd(e.target.value)
                                      }
                                    />
                                  </div>
                                  <Button
                                    type="button"
                                    size="sm"
                                    onClick={handleAddSlot}
                                    className="h-9"
                                  >
                                    Add
                                  </Button>
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => {
                                      setAddingForDay("");
                                      setSlotError("");
                                    }}
                                    className="h-9 px-2"
                                  >
                                    <X className="h-4 w-4" />
                                  </Button>
                                </div>
                                {slotError && (
                                  <p className="z-secondary text-destructive">
                                    {slotError}
                                  </p>
                                )}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </TabsContent>

                    {/* In Clinic — weekly column view */}
                    {hasClinics && (
                      <TabsContent value="clinic" className="mt-4 space-y-1">
                        {DAYS_OF_WEEK.map((day) => {
                          const daySlots = clinicSlots.filter(
                            (s) => s.day === day
                          );
                          const isAdding =
                            addingForDay === day && slotMode === "clinic";

                          return (
                            <div
                              key={day}
                              className="border rounded-lg overflow-hidden"
                            >
                              <div className="flex items-center p-3 gap-3">
                                <span className="w-12 shrink-0 text-sm font-medium">
                                  {DAY_SHORT_LABELS[day]}
                                </span>

                                {daySlots.length === 0 ? (
                                  <span className="text-sm text-muted-foreground flex-1">
                                    Unavailable
                                  </span>
                                ) : (
                                  <div className="flex flex-wrap gap-1.5 flex-1">
                                    {daySlots.map((slot, i) => {
                                      const idx = findSlotIndex(slot);
                                      return (
                                        <div
                                          key={i}
                                          className="flex items-center gap-1.5 bg-muted rounded-md px-2.5 py-1"
                                        >
                                          <span className="text-sm">
                                            {slot.start_time.slice(0, 5)} –{" "}
                                            {slot.end_time.slice(0, 5)}
                                          </span>
                                          <span className="text-xs text-muted-foreground">
                                            {slot.location}
                                          </span>
                                          {isEditing && (
                                            <button
                                              type="button"
                                              onClick={() =>
                                                handleRemoveSlot(idx)
                                              }
                                              className="text-muted-foreground hover:text-destructive transition-colors"
                                            >
                                              <X className="h-3 w-3" />
                                            </button>
                                          )}
                                        </div>
                                      );
                                    })}
                                  </div>
                                )}

                                {isEditing && (
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="icon"
                                    onClick={() =>
                                      openAddSlotForDay("clinic", day)
                                    }
                                    className="h-7 w-7 shrink-0"
                                  >
                                    <Plus className="h-3.5 w-3.5" />
                                  </Button>
                                )}
                              </div>

                              {isAdding && (
                                <div className="border-t bg-muted/50 p-3 space-y-2">
                                  <div>
                                    <label className="z-secondary text-muted-foreground mb-1 block">
                                      Clinic
                                    </label>
                                    <select
                                      value={slotClinicId ?? ""}
                                      onChange={(e) =>
                                        setSlotClinicId(e.target.value || null)
                                      }
                                      className="h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-ring"
                                    >
                                      <option value="">Select clinic</option>
                                      {clinics?.map((clinic) => (
                                        <option
                                          key={clinic.id}
                                          value={clinic.id}
                                        >
                                          {clinic.name}
                                        </option>
                                      ))}
                                    </select>
                                  </div>
                                  <div className="flex items-end gap-2">
                                    <div className="flex-1">
                                      <label className="z-secondary text-muted-foreground mb-1 block">
                                        Start Time
                                      </label>
                                      <Input
                                        type="time"
                                        value={slotStart}
                                        onChange={(e) =>
                                          setSlotStart(e.target.value)
                                        }
                                      />
                                    </div>
                                    <div className="flex-1">
                                      <label className="z-secondary text-muted-foreground mb-1 block">
                                        End Time
                                      </label>
                                      <Input
                                        type="time"
                                        value={slotEnd}
                                        onChange={(e) =>
                                          setSlotEnd(e.target.value)
                                        }
                                      />
                                    </div>
                                    <Button
                                      type="button"
                                      size="sm"
                                      onClick={handleAddSlot}
                                      className="h-9"
                                    >
                                      Add
                                    </Button>
                                    <Button
                                      type="button"
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => {
                                        setAddingForDay("");
                                        setSlotError("");
                                      }}
                                      className="h-9 px-2"
                                    >
                                      <X className="h-4 w-4" />
                                    </Button>
                                  </div>
                                  {slotError && (
                                    <p className="z-secondary text-destructive">
                                      {slotError}
                                    </p>
                                  )}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </TabsContent>
                    )}
                  </Tabs>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>

          {/* Submit — only visible in edit mode */}
          {isEditing && (
            <div className="flex justify-end pb-2">
              <Button
                type="submit"
                disabled={updateMutation.isPending}
                className="gap-2"
              >
                {updateMutation.isPending && (
                  <Loader2 className="h-4 w-4 animate-spin" />
                )}
                Save Changes
              </Button>
            </div>
          )}
        </form>
      </Form>

      {/* Toggle Status Confirmation */}
      <AlertDialog
        open={toggleConfirmOpen}
        onOpenChange={(open) => {
          if (!open) setToggleConfirmOpen(false);
        }}
      >
        <AlertDialogContent onCloseAutoFocus={(e) => e.preventDefault()}>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {localIsActive ? "Deactivate" : "Activate"}{" "}
              {staffDetail.first_name} {staffDetail.last_name}?
            </AlertDialogTitle>
            <AlertDialogDescription>
              {localIsActive
                ? "This will revoke their login access."
                : "This will restore their access to the platform."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              disabled={toggleMutation.isPending}
              onClick={() => setToggleConfirmOpen(false)}
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              disabled={toggleMutation.isPending}
              className={
                localIsActive
                  ? "bg-destructive hover:bg-destructive/90 text-destructive-foreground"
                  : ""
              }
              onClick={() => {
                const newStatus = !localIsActive;
                setLocalIsActive(newStatus); // optimistic — pill updates immediately
                setToggleConfirmOpen(false);
                toggleMutation.mutate(
                  { staffId, isActive: newStatus },
                  {
                    onError: () => setLocalIsActive(!newStatus), // revert on failure
                  }
                );
              }}
            >
              {localIsActive ? "Deactivate" : "Activate"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Success Dialog */}
      <AlertDialog open={successOpen} onOpenChange={setSuccessOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="text-foreground flex items-center gap-2">
              <Pencil className="h-5 w-5" />
              Staff Updated Successfully!
            </AlertDialogTitle>
            <AlertDialogDescription>
              Changes have been saved.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction onClick={handleSuccessOk}>OK</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Error Dialog — surfaces backend message when save fails */}
      <AlertDialog open={errorOpen} onOpenChange={setErrorOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="text-foreground flex items-center gap-2">
              <AlertCircle className="h-5 w-5 text-destructive" />
              Failed to Update Staff
            </AlertDialogTitle>
            <AlertDialogDescription className="whitespace-pre-line">
              {errorMessage}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction onClick={() => setErrorOpen(false)}>
              OK
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default EditStaffForm;
